import type {
  RiskProfilePropertiesResponse,
  PropertiesMetricOption,
} from '../types/riskProfileProperties.types'

export interface SocialEconomicRankingSeries {
  value: number
  unit: string | null
  percentValue: number | null
}

export interface SocialEconomicRankingChartData {
  labels: string[]
  values: number[]
  totals: SocialEconomicRankingSeries[]
}

export interface MapToSocialEconomicRankingOptions {
  metricOption: PropertiesMetricOption
  topN: number
}

export const mapRiskProfileSocialEconomicResponseToRanking = (
  response: RiskProfilePropertiesResponse,
  options: MapToSocialEconomicRankingOptions,
): SocialEconomicRankingChartData => {
  const labelByGroupKey = new Map(response.groups.map((group) => [group.key, group.label]))
  const rankingAttribute = options.metricOption.type === 'total' ? 'value' : 'percent_value'

  const ranked = [...response.group_totals]
    .sort((a, b) => (b[rankingAttribute] ?? 0) - (a[rankingAttribute] ?? 0))
    .slice(0, options.topN)

  return {
    labels: ranked.map((total) => labelByGroupKey.get(total.group_key) ?? total.group_key),
    values: ranked.map((total) =>
      options.metricOption.type === 'total' ? total.value : (total.percent_value ?? 0),
    ),
    totals: ranked.map((total) => ({
      value: total.value,
      unit: total.unit,
      percentValue: total.percent_value,
    })),
  }
}

export interface SocialEconomicSummaryCategory {
  key: string
  label: string
  value: number
  totalValue: number
  percentValue: number | null
  percentOfTotal: number
}

export interface SocialEconomicSummary {
  totalValue: number
  grandTotalValue: number
  percentValue: number | null
  unit: string | null
  categories: SocialEconomicSummaryCategory[]
}

export const mapRiskProfileSocialEconomicResponseToSummary = (
  response: RiskProfilePropertiesResponse,
  _metricOption: PropertiesMetricOption,
): SocialEconomicSummary | null => {
  if (response.groups.length === 0) return null

  const labelByGroupKey = new Map(response.groups.map((group) => [group.key, group.label]))
  const grandTotal = response.grand_total[0]

  const categories = response.group_totals.map((total) => ({
    key: total.group_key,
    label: labelByGroupKey.get(total.group_key) ?? total.group_key,
    value: total.value,
    totalValue: total.total_value,
    percentValue: total.percent_value,
    percentOfTotal: grandTotal && grandTotal.value > 0 ? (100 * total.value) / grandTotal.value : 0,
  }))

  return {
    totalValue: grandTotal?.value ?? 0,
    grandTotalValue: grandTotal?.total_value ?? 0,
    percentValue: grandTotal?.percent_value ?? null,
    unit: grandTotal?.unit ?? null,
    categories,
  }
}
